// Games playable at /games/:slug. Every bundle in public/games/ is an
// unmodified shareware release (checked against the original archive's
// checksums) plus a .jsdos/dosbox.conf that boots it.
//   doom, heretic: id Software idgames archive (gamers.org/pub/idgames/idstuff)
//   wolf3d (1wolf14.zip), keen1, jill, epicpin: Internet Archive items
//   w3d-box, keen1-sw, jill1-sw, epic_pinball_11

export type Control = { action: string; keys: string };

// js-dos (GLFW-style) key codes for ci.sendKeyEvent.
export const KEY = {
  esc: 256, enter: 257, space: 32, y: 89,
  up: 265, down: 264, left: 263, right: 262,
  lshift: 340, lctrl: 341, lalt: 342, rshift: 344,
  lbracket: 91, rbracket: 93,
  n1: 49, n2: 50, n3: 51, n4: 52, n5: 53, n6: 54, n7: 55,
} as const;

// On-screen button for touch devices; holds every key in `keys` together.
export type TouchButton = { label: string; keys: number[] };

const weapons = (count: number): TouchButton[] =>
  [KEY.n1, KEY.n2, KEY.n3, KEY.n4, KEY.n5, KEY.n6, KEY.n7]
    .slice(0, count)
    .map((k, i) => ({ label: String(i + 1), keys: [k] }));

export type Game = {
  slug: string;
  title: string;
  episode: string;
  year: number;
  publisher: string;
  genre: string;
  blurb: string;
  sizeMb: number;
  /** Gameplay screenshot used as the marketplace card cover. */
  cover: string;
  controls: Control[];
  // Touch layout: big action buttons beside the d-pad, small extras below.
  touch: { actions: TouchButton[]; extras?: TouchButton[] };
};

export const games: Game[] = [
  {
    slug: "doom",
    title: "DOOM",
    episode: "Episode 1: Knee-Deep in the Dead",
    year: 1993,
    publisher: "id Software",
    genre: "Shooter",
    blurb: "Fight your way out of a demon-infested Mars base. The one that started it all.",
    sizeMb: 2.1,
    cover: "/assets/games/doom.png",
    controls: [
      { action: "Move", keys: "Arrow keys (always runs)" },
      { action: "Fire", keys: "Ctrl" },
      { action: "Open doors / use", keys: "Space" },
      { action: "Strafe", keys: "Alt + arrows" },
      { action: "Weapons", keys: "1–7" },
      { action: "Menu", keys: "Esc" },
    ],
    touch: {
      actions: [
        { label: "Fire", keys: [KEY.lctrl] },
        { label: "Use", keys: [KEY.space] },
        { label: "Strafe", keys: [KEY.lalt] },
      ],
      extras: weapons(7),
    },
  },
  {
    slug: "wolf3d",
    title: "Wolfenstein 3D",
    episode: "Episode 1: Escape from Castle Wolfenstein",
    year: 1992,
    publisher: "id Software / Apogee",
    genre: "Shooter",
    blurb: "Break out of a Nazi fortress in the game that invented the first-person shooter.",
    sizeMb: 0.7,
    cover: "/assets/games/wolf3d.jpg",
    controls: [
      { action: "Move", keys: "Arrow keys (hold Shift to run)" },
      { action: "Fire", keys: "Ctrl" },
      { action: "Open doors", keys: "Space" },
      { action: "Strafe", keys: "Alt + arrows" },
      { action: "Weapons", keys: "1–4" },
      { action: "Menu", keys: "Esc" },
    ],
    touch: {
      actions: [
        { label: "Fire", keys: [KEY.lctrl] },
        { label: "Open", keys: [KEY.space] },
        { label: "Strafe", keys: [KEY.lalt] },
        { label: "Run", keys: [KEY.lshift] },
      ],
      extras: weapons(4),
    },
  },
  {
    slug: "heretic",
    title: "Heretic",
    episode: "Episode 1: City of the Damned",
    year: 1994,
    publisher: "Raven Software / id Software",
    genre: "Shooter",
    blurb: "DOOM's engine goes dark fantasy: wands, crossbows, and a whole lot of gargoyles.",
    sizeMb: 2.8,
    cover: "/assets/games/heretic.png",
    controls: [
      { action: "Move", keys: "Arrow keys (always runs)" },
      { action: "Fire", keys: "Ctrl" },
      { action: "Open doors / use", keys: "Space" },
      { action: "Strafe", keys: "Alt + arrows" },
      { action: "Inventory", keys: "[ and ] to pick, Enter to use" },
      { action: "Menu", keys: "Esc" },
    ],
    touch: {
      actions: [
        { label: "Fire", keys: [KEY.lctrl] },
        { label: "Use", keys: [KEY.space] },
        { label: "Strafe", keys: [KEY.lalt] },
      ],
      extras: [
        { label: "Item ◀", keys: [KEY.lbracket] },
        { label: "Item ▶", keys: [KEY.rbracket] },
        ...weapons(7),
      ],
    },
  },
  {
    slug: "keen1",
    title: "Commander Keen",
    episode: "Episode 1: Marooned on Mars",
    year: 1990,
    publisher: "id Software / Apogee",
    genre: "Platformer",
    blurb: "Eight-year-old genius Billy Blaze crash-lands on Mars and needs his ship parts back.",
    sizeMb: 0.2,
    cover: "/assets/games/keen1.png",
    controls: [
      { action: "Move", keys: "Arrow keys" },
      { action: "Jump", keys: "Ctrl" },
      { action: "Pogo stick", keys: "Alt" },
      { action: "Fire raygun", keys: "Ctrl + Alt" },
      { action: "Status", keys: "Space" },
      { action: "Menu", keys: "Esc" },
    ],
    touch: {
      actions: [
        { label: "Jump", keys: [KEY.lctrl] },
        { label: "Pogo", keys: [KEY.lalt] },
        { label: "Fire", keys: [KEY.lctrl, KEY.lalt] },
        { label: "Status", keys: [KEY.space] },
      ],
    },
  },
  {
    slug: "jill",
    title: "Jill of the Jungle",
    episode: "Episode 1: Jill of the Jungle",
    year: 1992,
    publisher: "Epic MegaGames",
    genre: "Platformer",
    blurb: "Epic's first big hit: swing through a 16-level jungle, turning into frogs and birds.",
    sizeMb: 0.3,
    cover: "/assets/games/jill.png",
    controls: [
      { action: "Move", keys: "Arrow keys" },
      { action: "Jump", keys: "Shift" },
      { action: "Throw weapon", keys: "Alt" },
      { action: "Menu / quit", keys: "Esc" },
    ],
    touch: {
      actions: [
        { label: "Jump", keys: [KEY.lshift] },
        { label: "Throw", keys: [KEY.lalt] },
      ],
    },
  },
  {
    slug: "epicpin",
    title: "Epic Pinball",
    episode: "Android table",
    year: 1993,
    publisher: "Epic MegaGames",
    genre: "Pinball",
    blurb: "Silky 256-color pinball that sold a mountain of shareware disks.",
    sizeMb: 0.7,
    cover: "/assets/games/epicpin.png",
    controls: [
      { action: "Flippers", keys: "Left Shift / Right Shift" },
      { action: "Launch ball", keys: "Space (hold and release)" },
      { action: "Menu / quit", keys: "Esc" },
    ],
    touch: {
      actions: [
        { label: "Left", keys: [KEY.lshift] },
        { label: "Right", keys: [KEY.rshift] },
        { label: "Launch", keys: [KEY.space] },
      ],
    },
  },
];

export const findGame = (slug: string | undefined) => games.find((g) => g.slug === slug);
